/*
 * Where the pharmacy is, written into the address after the #, so refreshing the page keeps you
 * there and the browser's Back button goes where you expect.
 *
 *   #/                                 Home — the overview
 *   #/homes                            Care homes
 *   #/homes/new                        Care homes, adding one
 *   #/homes/<id>                       One home
 *   #/homes/<id>/<tab>                 One home, on a tab
 *   #/homes/<id>/deliveries/<id>       One home, open on a delivery
 *   #/deliveries                       Every delivery
 *   #/deliveries/<show>                Every delivery, filtered
 *   #/calendar                         This month
 *   #/calendar/2026-10                 A month
 */
import { isHomeTab } from './home-view.tsx';
import type { HomeTab } from './home-view.tsx';

export type DeliveryShow = 'waiting' | 'drafts' | 'done' | 'all';

export type Route =
  | { page: 'overview' }
  | { page: 'homes'; adding: boolean }
  | { page: 'home'; homeId: string; tab: HomeTab | null; deliveryId: string | null }
  | { page: 'deliveries'; show: DeliveryShow }
  | { page: 'calendar'; month: string | null };

export type Go = (route: Route) => void;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHOWS: DeliveryShow[] = ['waiting', 'drafts', 'done', 'all'];

export function parseHash(hash: string): Route {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean);
  const [first, second, third, fourth] = parts;

  if (first === 'homes') {
    if (!second) return { page: 'homes', adding: false };
    if (second === 'new') return { page: 'homes', adding: true };
    if (UUID.test(second)) {
      const tab = isHomeTab(third) ? third : null;
      const deliveryId = tab === 'deliveries' && fourth && UUID.test(fourth) ? fourth : null;
      return { page: 'home', homeId: second, tab, deliveryId };
    }
    return { page: 'homes', adding: false };
  }
  if (first === 'deliveries') {
    const show = SHOWS.includes(second as DeliveryShow) ? (second as DeliveryShow) : 'waiting';
    return { page: 'deliveries', show };
  }
  if (first === 'calendar') {
    return { page: 'calendar', month: second && /^\d{4}-\d{2}$/.test(second) ? second : null };
  }
  return { page: 'overview' };
}

export function hashFor(route: Route): string {
  switch (route.page) {
    case 'overview':
      return '#/';
    case 'homes':
      return route.adding ? '#/homes/new' : '#/homes';
    case 'home': {
      let hash = `#/homes/${route.homeId}`;
      if (route.tab) hash += `/${route.tab}`;
      if (route.tab === 'deliveries' && route.deliveryId) hash += `/${route.deliveryId}`;
      return hash;
    }
    case 'deliveries':
      return route.show === 'waiting' ? '#/deliveries' : `#/deliveries/${route.show}`;
    case 'calendar':
      return route.month ? `#/calendar/${route.month}` : '#/calendar';
  }
}

/* Which sidebar entry is lit. A single home belongs under Care homes. */
export function sectionOf(route: Route): 'overview' | 'homes' | 'deliveries' | 'calendar' {
  return route.page === 'home' ? 'homes' : route.page;
}
